import * as React from "react";
import { Marker } from "react-simple-maps";
import FmdGoodIcon from "@mui/icons-material/FmdGood";

import { Pin, Pins } from "./types";

import theme from "../../../../../components/styles/theme/theme";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../store/store";
import { isRegion } from "../../../../../config";

type Props = {
  data: Pins;
  excludeStates?: string[];
};

const renderMarkers = (data: Pins, selectedCountryName: string, zoom: number) => {
  const baseSize = selectedCountryName === "Mexico" ? 3 : 5;
  // Scale pins inversely with zoom so they don't dominate small states
  const baseZoom = isRegion.usa ? 2.5 : 1;
  const value = Math.max(0.5, baseSize * baseZoom / zoom);
  const pinColor = isRegion.usa ? theme.palette.secondary[600] : theme.palette.common.black;
  return data.features.map((pin: Pin) => {
    const { properties } = pin;
    const { id, name } = properties;

    return (
      <Marker
        key={id}
        coordinates={[pin.geometry.coordinates[0], pin.geometry.coordinates[1]]}
        data-tooltip-content={
          name || "We have no found information about that location"
        }
        data-tooltip-id="my-tooltip"
        data-tooltip-offset={10}
        style={{
          default: {
            outline: "none",
            fill: pinColor,
            stroke: theme.palette.common.white,
            strokeWidth: 0.3,
          },
          hover: {
            outline: "none",
            fill: pinColor,
            stroke: theme.palette.common.white,
            strokeWidth: 0.6,
          },
          pressed: {
            outline: "none",
            fill: pinColor,
            stroke: theme.palette.common.white,
            strokeWidth: 0.6,
          },
        }}
      >
        <g transform={`translate(-${value / 2}, -${value / 2})`}>
          <FmdGoodIcon width={value} height={value} />
        </g>
      </Marker>
    );
  });
};

export const Markers: React.FC<Props> = ({ data, excludeStates }) => {
  const selectedCountryName = useSelector(
    (state: RootState) => state.map.selectedCountry?.country_name || ""
  );
  const selectedState = useSelector(
    (state: RootState) => state.map.selectedState
  );

  // Filter pins by selected state when drilled down, and exclude states outside projection
  let filteredFeatures = data?.features || [];
  if (selectedState) {
    filteredFeatures = filteredFeatures.filter((pin) =>
      pin.properties.state?.toUpperCase() === selectedState.state_name.toUpperCase()
    );
  }
  if (excludeStates?.length) {
    const excluded = new Set(excludeStates.map(s => s.toUpperCase()));
    filteredFeatures = filteredFeatures.filter((pin) =>
      !excluded.has(pin.properties.state?.toUpperCase() || '')
    );
  }
  const filteredData = data ? { ...data, features: filteredFeatures } : data;

  const zoom = useSelector((state: RootState) => state.map.zoom);

  return <>{filteredData && renderMarkers(filteredData, selectedCountryName, zoom)}</>;
};

export default Markers;
